import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronDown, ChevronRight, Info } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { unwrap } from '../../lib/api-envelope';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { fmtSignedCurrency } from '../../lib/display-labels';
import {
  BILLING_FLAG_TYPE_LABEL,
  BILLING_MODES,
  BILLING_MODE_COPY,
  acceptedPhrase,
  billingFlagTypeLabel,
  checksHeadline,
  dollarsCorrectedParts,
  metricMonthWindow,
  monthLabel,
  notYetPushedPhrase,
  officeHoursHeader,
  officeHoursPhrase,
  rulePrecisionPhrase,
  shadowOutcomeLabel,
  suggestChecksSentence,
} from '../../lib/billing-agent';
import type {
  BillingMetricsMonth,
  BillingPreflightMetrics,
  BillingPreflightMode,
  BillingPreflightSettings,
  BillingShadowReport,
} from '../../types/billing-flags';

/**
 * Settings → AI & integrations → Billing agent (brief FE-2; api#908, api#916).
 *
 * Four sections, each loading and failing on its own:
 *   1. Mode — off / shadow / suggest. Anyone who can read it sees it; only an
 *      `admin` may change it, because the PUT is STRICT admin (an owner and an
 *      elevated dispatcher are refused). Suggest asks first.
 *   1b. Checks — which rules are active (proven) and which are still learning
 *      (brief FE-3, api#938). Read-only for EVERYONE: proven status is the
 *      platform's decision, and the tenant PUT refuses it with a 403.
 *   2. The three metrics, the tenant-local current month and the two before it.
 *   3. Shadow — what the agent would have flagged, scored against what the
 *      office did without it.
 *
 * Every figure is the API's. Nothing here adds, nets or averages one, and the
 * shadow $ is NEVER folded into $ corrected: the first is "the office agreed
 * without seeing the flag", the second is "the office changed a ticket because
 * of a flag", and a sum of the two answers neither.
 *
 * The AI & integrations workspace renders the heading and the tab strip; this
 * page owns neither a heading nor a back arrow.
 */

export const BILLING_SETTINGS_KEY = ['billing-preflight', 'settings'] as const;
const METRICS_KEY = ['billing-preflight', 'metrics'] as const;
const SHADOW_REPORT_KEY = ['billing-preflight', 'shadow-report'] as const;
const SHADOW_REPORT_DAYS = 30;

const PANEL = 'rounded-fi-panel border border-fieldiq-border bg-fieldiq-card';
const EYEBROW = 'text-[11px] font-semibold uppercase tracking-wide text-fieldiq-muted';

function Pulse({ label }: { label: string }) {
  return <span role="status" aria-label={label} className="inline-block h-4 w-20 animate-pulse rounded bg-fieldiq-border" />;
}

function useBillingSettings() {
  return useQuery({
    queryKey: BILLING_SETTINGS_KEY,
    queryFn: async () => unwrap<BillingPreflightSettings>((await api.billingPreflight.getSettings()).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

function useBillingMetrics(months: string[] | null) {
  const from = months?.[0];
  const to = months?.[months.length - 1];
  return useQuery({
    queryKey: [...METRICS_KEY, from, to],
    queryFn: async () => unwrap<BillingPreflightMetrics>((await api.billingPreflight.metrics(from!, to!)).data),
    enabled: !!from && !!to,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

// ─── 1. Mode ─────────────────────────────────────────────────────────────────

function ModeSection() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  // Strict admin, as the API's PUT is. isAdminRole would admit an owner the API refuses.
  const canChange = user?.role === 'admin';
  const [confirmSuggest, setConfirmSuggest] = useState(false);

  const query = useBillingSettings();
  const failure = queryLoadFailure(query);
  const current = query.data?.mode ?? null;
  const counts = query.data?.counts;
  const checksSentence = suggestChecksSentence(counts);

  const mutation = useMutation({
    mutationFn: (mode: BillingPreflightMode) => api.billingPreflight.setMode(mode),
    onSuccess: (_res, mode) => {
      toast.success(`Billing agent set to ${BILLING_MODE_COPY[mode].label}`);
      setConfirmSuggest(false);
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to change the billing agent mode'),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: BILLING_SETTINGS_KEY });
      queryClient.invalidateQueries({ queryKey: METRICS_KEY });
      // Every ticket's panel reads the mode off its own payload.
      queryClient.invalidateQueries({ queryKey: ['billing-flags'] });
    },
  });

  const choose = (mode: BillingPreflightMode) => {
    if (!canChange || mode === current || mutation.isPending) return;
    if (mode === 'suggest') setConfirmSuggest(true);
    else mutation.mutate(mode);
  };

  return (
    <section aria-labelledby="billing-agent-mode" className={`${PANEL} border-l-4 border-l-fi-amber`} data-testid="billing-agent-mode">
      <div className="px-4 pt-4 pb-2">
        <p className={EYEBROW}>Billing agent</p>
        <h2 id="billing-agent-mode" className="text-base font-semibold text-white">Mode</h2>
      </div>
      <div className="px-4 pb-4">
        {failure ? (
          <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="the billing agent's mode" allowed="admins and office staff" />
        ) : query.isLoading || !current ? (
          <Pulse label="Loading the mode" />
        ) : (
          <div role="radiogroup" aria-label="Billing agent mode" aria-readonly={!canChange} className="grid gap-2 md:grid-cols-3">
            {BILLING_MODES.map((mode) => {
              const selected = mode === current;
              return (
                <button
                  key={mode}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  disabled={!canChange || mutation.isPending}
                  onClick={() => choose(mode)}
                  className={`text-left rounded-lg border p-3 transition-colors ${
                    selected ? 'border-fieldiq-accent bg-fieldiq-dark' : 'border-fieldiq-border'
                  } ${canChange ? 'hover:border-fieldiq-accent' : 'cursor-default'} disabled:opacity-100`}
                >
                  <span className="flex items-center gap-2 text-sm font-semibold text-white">
                    {selected && <Check size={14} aria-hidden className="text-fieldiq-accent" />}
                    {BILLING_MODE_COPY[mode].label}
                  </span>
                  <span className="mt-1 block text-xs text-fieldiq-muted">{BILLING_MODE_COPY[mode].sentence}</span>
                </button>
              );
            })}
          </div>
        )}
        {/* Suggest with nothing proven shows the office nothing at all, which an
            admin would otherwise read as broken. Only on a real 0 from the API. */}
        {current === 'suggest' && counts?.active === 0 && (
          <p role="note" data-testid="suggest-no-active-checks"
            className="mt-3 flex items-start gap-2 rounded-lg border border-fieldiq-border bg-fieldiq-dark px-3 py-2 text-sm text-fieldiq-text">
            <Info size={14} aria-hidden className="mt-0.5 shrink-0 text-fieldiq-muted" />
            No checks are active yet, so office staff won't see any flags.
          </p>
        )}
      </div>
      {!canChange && !failure && (
        <p className="border-t border-fieldiq-border px-4 py-2 text-xs text-fieldiq-muted">Only an admin can change this.</p>
      )}

      <ConfirmModal
        isOpen={confirmSuggest}
        title="Show the agent's checks to the office?"
        body={
          <>
            <p>Office staff will see flags from active checks on each field-approved ticket. It never changes a ticket and never blocks approval.</p>
            {checksSentence && <p className="mt-2" data-testid="suggest-checks-count">{checksSentence}</p>}
          </>
        }
        confirmLabel="Switch to Suggest"
        confirmVariant="primary"
        loading={mutation.isPending}
        onConfirm={() => mutation.mutate('suggest')}
        onCancel={() => setConfirmSuggest(false)}
      />
    </section>
  );
}

// ─── 1b. Checks ──────────────────────────────────────────────────────────────

/**
 * Proven (Active) or not (Still learning). The two values of a boolean, so the
 * map is exhaustive; green for a check the office sees, slate for one it does
 * not. Dark ink on the saturated fill, the register badge idiom.
 */
const RULE_STATUS_BADGE = {
  active: { label: 'Active', fill: 'bg-fi-signal-green' },
  learning: { label: 'Still learning', fill: 'bg-fi-signal-slate' },
} as const;

function ChecksSection() {
  const query = useBillingSettings();
  const failure = queryLoadFailure(query);
  const rules = query.data?.rules;

  return (
    <section aria-labelledby="billing-agent-checks" className={PANEL} data-testid="billing-agent-checks">
      <div className="px-4 pt-4 pb-2">
        <p className={EYEBROW}>Checks</p>
        <h2 id="billing-agent-checks" className="text-base font-semibold text-white" data-testid="checks-headline">
          {failure ? 'Checks' : query.isLoading || !query.data ? <Pulse label="Loading the checks" /> : checksHeadline(query.data.counts)}
        </h2>
      </div>
      {failure ? (
        <div className="px-4 pb-4">
          <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="the billing agent's checks" allowed="admins and office staff" />
        </div>
      ) : query.isLoading || !query.data ? null : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[480px] text-sm" aria-label="Billing checks">
              <thead>
                <tr className="border-b border-fieldiq-border text-left text-xs text-fieldiq-muted">
                  <th scope="col" className="px-3 py-2 font-medium">Check</th>
                  <th scope="col" className="px-3 py-2 font-medium">Status</th>
                  <th scope="col" className="px-3 py-2 font-medium">Precision</th>
                </tr>
              </thead>
              <tbody>
                {(rules ?? []).map((r) => {
                  const badge = RULE_STATUS_BADGE[r.proven ? 'active' : 'learning'];
                  return (
                    <tr key={`${r.flagType}@${r.ruleVersion}`} className="border-b border-fieldiq-border last:border-b-0 align-top"
                      data-testid={`check-${r.flagType}`}>
                      <th scope="row" className="px-3 py-2 text-left font-medium text-fieldiq-text">{r.label || '—'}</th>
                      <td className="px-3 py-2">
                        <span className={`inline-flex w-fit items-center rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink ${badge.fill}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <span className="tabular-nums text-fieldiq-text">{rulePrecisionPhrase(r.precision)}</span>
                        {r.precision === null && <span className="ml-2 text-xs text-fieldiq-muted">not measured yet</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="border-t border-fieldiq-border px-4 py-2 text-xs text-fieldiq-muted">
            FieldIQ switches a check on once it's proven accurate on real tickets. Until then it runs silently and nobody sees its flags.
          </p>
        </>
      )}
    </section>
  );
}

// ─── 2. Metrics ──────────────────────────────────────────────────────────────

function DollarsCell({ m }: { m: BillingMetricsMonth }) {
  const d = m.dollarsCorrected;
  const parts = dollarsCorrectedParts(d);
  return (
    <td className="px-3 py-2 align-top" data-testid={`dollars-${m.month}`}>
      <span className="tabular-nums text-fieldiq-text">{parts.up} / {parts.down} / {parts.net}</span>
      {d && d.net === null && d.excludedDeleted > 0 && (
        <span className="block text-xs text-fieldiq-muted">
          {d.excludedDeleted} resolved {d.excludedDeleted === 1 ? 'flag was' : 'flags were'} on a deleted ticket
        </span>
      )}
    </td>
  );
}

function MetricsSection({ months }: { months: string[] | null }) {
  const timeZone = useTenantTimeZone();
  const query = useBillingMetrics(months);
  const failure = queryLoadFailure(query);
  const rows = [...(query.data?.months ?? [])].reverse();   // newest first
  const sources = new Set(rows.map((r) => r.officeHours?.source));
  const source = sources.size === 1 ? rows[0]?.officeHours?.source : null;
  const loading = !failure && (!query.data || query.isLoading);

  return (
    <section aria-labelledby="billing-agent-metrics" className={`${PANEL} border-l-4 border-l-fi-amber`} data-testid="billing-agent-metrics">
      <div className="px-4 pt-4 pb-2">
        <p className={EYEBROW}>Last three months</p>
        <h2 id="billing-agent-metrics" className="text-base font-semibold text-white">What the agent changed</h2>
      </div>
      {failure ? (
        <div className="px-4 pb-4">
          <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="the billing agent's metrics" allowed="admins and office staff" />
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b border-fieldiq-border text-left text-xs text-fieldiq-muted">
                  <th scope="col" className="px-3 py-2 font-medium">Month</th>
                  <th scope="col" className="px-3 py-2 font-medium">$ corrected (up / down / net)</th>
                  <th scope="col" className="px-3 py-2 font-medium">Flags accepted</th>
                  <th scope="col" className="px-3 py-2 font-medium" data-testid="office-hours-header">{officeHoursHeader(source)}</th>
                </tr>
              </thead>
              <tbody>
                {loading
                  ? (months ?? ['a', 'b', 'c']).map((m) => (
                    <tr key={m} className="border-b border-fieldiq-border last:border-b-0">
                      <td className="px-3 py-2 text-fieldiq-text">{monthLabel(m)}</td>
                      <td className="px-3 py-2"><Pulse label="Loading" /></td>
                      <td className="px-3 py-2"><Pulse label="Loading" /></td>
                      <td className="px-3 py-2"><Pulse label="Loading" /></td>
                    </tr>
                  ))
                  : rows.map((m) => {
                    const pushed = notYetPushedPhrase(m.officeHours);
                    return (
                      <tr key={m.month} className="border-b border-fieldiq-border last:border-b-0" data-testid={`metrics-row-${m.month}`}>
                        <th scope="row" className="px-3 py-2 text-left font-medium text-fieldiq-text align-top">{monthLabel(m.month)}</th>
                        <DollarsCell m={m} />
                        <td className="px-3 py-2 align-top" data-testid={`accepted-${m.month}`}>
                          <span className="tabular-nums text-fieldiq-text">{acceptedPhrase(m.accepted)}</span>
                          {m.accepted && m.accepted.open > 0 && (
                            <span className="ml-2 text-xs text-fieldiq-muted">{m.accepted.open} open</span>
                          )}
                        </td>
                        <td className="px-3 py-2 align-top" data-testid={`hours-${m.month}`}>
                          <span className="tabular-nums text-fieldiq-text">{officeHoursPhrase(m.officeHours)}</span>
                          {source === null && m.officeHours?.label && (
                            <span className="block text-xs text-fieldiq-muted">{m.officeHours.label}</span>
                          )}
                          {pushed && <span className="block text-xs text-fieldiq-muted">{pushed}</span>}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
          <div className="border-t border-fieldiq-border px-4 py-2 text-xs text-fieldiq-muted space-y-0.5">
            {source === 'office_approval' && <p data-testid="office-hours-interim">No invoices pushed to QuickBooks yet.</p>}
            <p>Months are calendar months in the company's time zone{timeZone ? ` (${timeZone})` : ''}.</p>
          </div>
        </>
      )}
    </section>
  );
}

// ─── 3. Shadow ───────────────────────────────────────────────────────────────

function ShadowReportTable() {
  const query = useQuery({
    queryKey: [...SHADOW_REPORT_KEY, SHADOW_REPORT_DAYS],
    queryFn: async () => unwrap<BillingShadowReport>((await api.billingPreflight.shadowReport(SHADOW_REPORT_DAYS)).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failure = queryLoadFailure(query);
  if (failure) {
    return <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="the shadow report" allowed="admins and office staff" />;
  }
  if (query.isLoading || !query.data) return <Pulse label="Loading the shadow report" />;

  // Known types in the labels' order, then any type this build does not know.
  const types = Object.keys(query.data.byType ?? {}).sort((a, b) => {
    const order = Object.keys(BILLING_FLAG_TYPE_LABEL);
    const ia = order.indexOf(a); const ib = order.indexOf(b);
    return (ia < 0 ? order.length : ia) - (ib < 0 ? order.length : ib);
  });
  const rows = types.flatMap((t) => query.data!.byType[t].flags.map((f) => ({ ...f, type: t })));
  if (rows.length === 0) {
    return <p className="text-sm text-fieldiq-muted">The agent raised no shadow flags in the last {SHADOW_REPORT_DAYS} days.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[640px] text-sm" aria-label="Shadow flags">
        <thead>
          <tr className="border-b border-fieldiq-border text-left text-xs text-fieldiq-muted">
            <th scope="col" className="px-3 py-2 font-medium">Check</th>
            <th scope="col" className="px-3 py-2 font-medium">Ticket</th>
            <th scope="col" className="px-3 py-2 font-medium">Message</th>
            <th scope="col" className="px-3 py-2 font-medium">Outcome</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((f) => (
            <tr key={f.flagId} className="border-b border-fieldiq-border last:border-b-0 align-top">
              <td className="px-3 py-2 text-fieldiq-text">{billingFlagTypeLabel(f.type)}</td>
              <td className="px-3 py-2">
                <Link to={`/tickets/${f.ticketId}`} className="text-fieldiq-text underline underline-offset-2 hover:text-white">
                  {f.ticketNumber || '—'}
                </Link>
              </td>
              <td className="px-3 py-2 text-fieldiq-text">{f.message}</td>
              <td className="px-3 py-2 text-fieldiq-text">{shadowOutcomeLabel(f.outcome)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ShadowSection({ months }: { months: BillingMetricsMonth[] }) {
  const [open, setOpen] = useState(false);
  return (
    <section aria-labelledby="billing-agent-shadow" className={PANEL} data-testid="billing-agent-shadow">
      <div className="px-4 pt-4 pb-2">
        <p className={EYEBROW}>Shadow</p>
        <h2 id="billing-agent-shadow" className="text-base font-semibold text-white">
          Office reached the same conclusion without the agent
        </h2>
      </div>
      <ul className="px-4 pb-3 space-y-1 text-sm">
        {[...months].reverse().map((m) => (
          <li key={m.month} data-testid={`shadow-${m.month}`} className="text-fieldiq-text">
            <span className="font-medium">{monthLabel(m.month)}:</span>{' '}
            {m.shadow ? (
              <span className="tabular-nums">
                {m.shadow.agreed} agreed, {m.shadow.unconfirmed} not acted on, {m.shadow.missed} missed,{' '}
                {fmtSignedCurrency(m.shadow.agreedDollars?.up)} up / {fmtSignedCurrency(m.shadow.agreedDollars?.down)} down
              </span>
            ) : (
              <span className="text-fieldiq-muted">no shadow-checked ticket was office-approved</span>
            )}
          </li>
        ))}
      </ul>
      <div className="border-t border-fieldiq-border px-4 py-3">
        <button
          type="button"
          aria-expanded={open}
          aria-controls="billing-agent-shadow-report"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-1 text-sm font-medium text-white hover:underline"
        >
          {open ? <ChevronDown size={14} aria-hidden /> : <ChevronRight size={14} aria-hidden />}
          Shadow flags, last {SHADOW_REPORT_DAYS} days
        </button>
        <div id="billing-agent-shadow-report" className="mt-3">
          {open && <ShadowReportTable />}
        </div>
      </div>
    </section>
  );
}

// ─── the page ────────────────────────────────────────────────────────────────

export default function BillingAgentPage() {
  const timeZone = useTenantTimeZone();
  const months = metricMonthWindow(timeZone);
  // Same keys as the sections' own queries, so these make no second request.
  const settings = useBillingSettings();
  const metrics = useBillingMetrics(months).data;
  const monthRows = metrics?.months ?? [];
  const showShadow = settings.data?.mode === 'shadow' || monthRows.some((m) => m.shadow);

  return (
    <div className="min-h-full">
      <div className="px-6 py-4 space-y-4 pb-[var(--bottom-chrome)]">
        <p className="text-xs text-fieldiq-muted">
          Before the office approves a field ticket, the agent checks it against the rate sheet and raises flags the office resolves.
        </p>
        <ModeSection />
        <ChecksSection />
        <MetricsSection months={months} />
        {showShadow && <ShadowSection months={monthRows} />}
      </div>
    </div>
  );
}
